/**
 * Content extent: where each element ends up, which ones run off the label,
 * and how many label lengths the content would need.
 *
 * Replaces ns-web resources/js/components/common/label-viewer/utils/page-splitter.js
 * NS splits overflowing content into extra physical labels because its print
 * path prints those extra ^XA…^XZ blocks. Phoenix's server sends one label and
 * the printer clips anything past the print width or label length, so the
 * preview reports the overflow (pageCount, clipped elements) rather than
 * inventing pages. Text extents come from layoutText(), the same function the
 * renderer draws with.
 */

import type { BarcodeElement, LabelDoc, LabelElement, Rotation } from './document-model';
import { type MeasureText, layoutText } from './text-layout';
import { type BarcodeSize, encodeBarcode, encodedSize, loadedBarcodeEncoder } from './barcode-renderer';
import { BARCODE_DISPLAY_NAME, MATRIX_TYPES, printsEmptySymbol } from './barcode-types';
import { mmToDots } from './unit-converter';

export interface LabelGeometry {
  widthMm: number;
  heightMm: number;
  dpi: number;
}

export interface ElementBox {
  index: number;
  type: LabelElement['type'];
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Dots an element extends past each edge of the printable area (0 = inside). */
export interface EdgeOverflow {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface ClippedElement extends ElementBox {
  /** Human-readable name, e.g. 'Code 128 barcode "CA9999X0…"'. */
  name: string;
  overflow: EdgeOverflow;
}

/**
 * Overflow up to this many millimetres isn't flagged as clipping: a template's
 * rounding (e.g. a separator ^GB443 at x 14 on a ^PW456 label, 1 dot over)
 * loses ink nobody can see. Such elements are listed in trimmed instead.
 */
export const CLIP_TOLERANCE_MM = 0.5;

export interface ContentExtent {
  labelWidthDots: number;
  labelHeightDots: number;
  /** Right-most and bottom-most dot any element reaches. */
  rightDots: number;
  bottomDots: number;
  /** Label lengths the content spans (1 when everything fits). */
  pageCount: number;
  /** Elements that cross the print width or label length by more than CLIP_TOLERANCE_MM. */
  clipped: ClippedElement[];
  /** Elements over an edge by at most CLIP_TOLERANCE_MM (invisible rounding). */
  trimmed: ClippedElement[];
  /** ^FB text fields whose text didn't fit their line limit (the printer overprints the last line). */
  overprinted: number[];
  /** Boxes of the elements that print ink (empty text and data-less barcodes are left out). */
  boxes: ElementBox[];
}

/** CLIP_TOLERANCE_MM in whole printer dots (4 at 203 dpi, 6 at 300). */
export function clipToleranceDots(dpi: number): number {
  return mmToDots(CLIP_TOLERANCE_MM, dpi);
}

/** Media size in dots for the given physical size and resolution. */
export function labelSizeDots(geometry: LabelGeometry): { width: number; height: number } {
  const width = mmToDots(geometry.widthMm, geometry.dpi);
  const height = mmToDots(geometry.heightMm, geometry.dpi);
  return {
    width: Number.isFinite(width) && width > 0 ? width : mmToDots(57.15, geometry.dpi),
    height: Number.isFinite(height) && height > 0 ? height : mmToDots(38.1, geometry.dpi),
  };
}

/** Rough barcode size when bwip-js hasn't been loaded yet. */
function estimateBarcodeSize(el: BarcodeElement): BarcodeSize {
  if (MATRIX_TYPES.has(el.barcodeType)) {
    const mag = 'magnification' in el.options && el.options.magnification > 0 ? el.options.magnification : 3;
    const len = el.data.length || 1;
    const modules = len <= 17 ? 21 : len <= 32 ? 25 : len <= 53 ? 29 : len <= 78 ? 33 : 37;
    return { w: modules * mag, h: modules * mag };
  }
  const moduleWidth = 'moduleWidth' in el.options ? el.options.moduleWidth : 2;
  return { w: (el.data.length * 11 + 35) * moduleWidth, h: el.h };
}

/**
 * False for fields the printer draws nothing for: empty text, and a barcode
 * with empty data unless the printer still prints a stub for it (Code 128).
 */
export function hasInk(el: LabelElement): boolean {
  if (el.type === 'text') return el.text.length > 0;
  if (el.type === 'barcode') return el.data.length > 0 || printsEmptySymbol(el.barcodeType);
  return true;
}

/** Size of a barcode in dots, unrotated. */
export function barcodeSize(el: BarcodeElement, dpi: number, problems?: string[]): BarcodeSize {
  const encoder = loadedBarcodeEncoder();
  if (!encoder || (!el.data && !printsEmptySymbol(el.barcodeType))) return estimateBarcodeSize(el);
  try {
    return encodedSize(encodeBarcode(encoder, el, dpi, problems));
  } catch {
    return estimateBarcodeSize(el);
  }
}

/** Bounding box after a field rotation: 90/270 swap width and height. */
export function rotatedBox(w: number, h: number, rotate: Rotation): { w: number; h: number } {
  return rotate === 90 || rotate === 270 ? { w: h, h: w } : { w, h };
}

export function elementBox(el: LabelElement, index: number, dpi: number, measure?: MeasureText): ElementBox {
  switch (el.type) {
    case 'text': {
      const layout = layoutText(el, measure);
      const r = rotatedBox(layout.width, layout.height, el.rotate);
      return { index, type: el.type, x: el.x, y: el.y, w: r.w, h: r.h };
    }
    case 'barcode': {
      const size = barcodeSize(el, dpi);
      const r = rotatedBox(size.w, size.h, el.rotate);
      return { index, type: el.type, x: el.x, y: el.y, w: r.w, h: r.h };
    }
    case 'box':
    case 'diagonal':
      return { index, type: el.type, x: el.x, y: el.y, w: el.w, h: el.h };
    case 'circle':
      return { index, type: el.type, x: el.x, y: el.y, w: el.diameter, h: el.diameter };
    case 'graphic': {
      const rows = el.rowBytes ? Math.ceil(el.bytes.length / el.rowBytes) : 0;
      return { index, type: el.type, x: el.x, y: el.y, w: el.rowBytes * 8, h: rows };
    }
  }
}

function truncate(text: string, max = 24): string {
  return text.length > max ? text.slice(0, max - 1) + '…' : text;
}

/** Human-readable element name for warnings. */
export function elementName(el: LabelElement): string {
  switch (el.type) {
    case 'text':
      return `Text "${truncate(el.text)}"`;
    case 'barcode':
      return `${BARCODE_DISPLAY_NAME[el.barcodeType]} barcode "${truncate(el.data, 12)}"`;
    case 'box':
      return el.h <= el.thickness || el.w <= el.thickness ? 'Line' : 'Box';
    case 'circle':
      return 'Circle';
    case 'diagonal':
      return 'Diagonal line';
    case 'graphic':
      return 'Graphic';
  }
}

/** Plain-language description of a clipped element, e.g. for the details list. */
export function describeClipping(c: ClippedElement): string {
  const parts = (['right', 'bottom', 'left', 'top'] as const)
    .filter((edge) => c.overflow[edge] > 0)
    .map((edge) => `${Math.ceil(c.overflow[edge])} dot${Math.ceil(c.overflow[edge]) === 1 ? '' : 's'} past the ${edge} edge`);
  return `${c.name} at ${c.x},${c.y} extends ${parts.join(' and ')}`;
}

/**
 * Measure where the label's content lands. Pass the renderer's measure to use
 * real font widths; without it, widths are estimated from the font metrics.
 */
export function measureContent(doc: LabelDoc, geometry: LabelGeometry, measure?: MeasureText): ContentExtent {
  const size = labelSizeDots(geometry);
  const printWidth = doc.meta.widthDots ? Math.min(doc.meta.widthDots, size.width) : size.width;
  const boxes: ElementBox[] = [];
  const clipped: ClippedElement[] = [];
  const trimmed: ClippedElement[] = [];
  // Compare whole dots: 0.5 mm is 3.996 dots at 203 dpi, which must still allow a 4-dot overrun.
  const tolerance = clipToleranceDots(geometry.dpi);
  const overprinted: number[] = [];
  let rightDots = 0;
  let bottomDots = 0;

  doc.elements.forEach((el, index) => {
    if (!hasInk(el)) return;
    const box = elementBox(el, index, geometry.dpi, measure);
    boxes.push(box);
    rightDots = Math.max(rightDots, box.x + box.w);
    bottomDots = Math.max(bottomDots, box.y + box.h);
    const overflow: EdgeOverflow = {
      left: Math.max(0, -box.x),
      top: Math.max(0, -box.y),
      right: Math.max(0, box.x + box.w - printWidth),
      bottom: Math.max(0, box.y + box.h - size.height),
    };
    const worst = Math.max(overflow.left, overflow.top, overflow.right, overflow.bottom);
    if (worst > 0) (worst > tolerance ? clipped : trimmed).push({ ...box, name: elementName(el), overflow });
    if (el.type === 'text' && el.block && layoutText(el, measure).overflowed) overprinted.push(index);
  });

  return {
    labelWidthDots: size.width,
    labelHeightDots: size.height,
    rightDots,
    bottomDots,
    pageCount: Math.max(1, Math.ceil(bottomDots / size.height)),
    clipped,
    trimmed,
    overprinted,
    boxes,
  };
}
